import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Zap, Heart, Target, Users, Award, Rocket, GraduationCap } from 'lucide-react';

const teamValues = [
  { icon: Heart, title: 'Student-First', desc: 'Every feature is designed with students in mind. We make learning fun, not stressful.' },
  { icon: Target, title: 'CBSE-Focused', desc: 'Built specifically for Indian curriculum. We know what CBSE students need to succeed.' },
  { icon: Rocket, title: 'AI-Powered', desc: 'Cutting-edge AI that generates personalized study content in seconds.' },
  { icon: Users, title: 'Family Connect', desc: 'We believe parents and teachers play a crucial role in student success.' },
];

const milestones = [
  { year: '2024', event: 'StudyClaw founded with a mission to revolutionize exam prep' },
  { year: '2025', event: 'Launched AI Chapter Essence and Smart Quiz features' },
  { year: '2025', event: 'Expanded to cover all NCERT chapters (351+) for Classes 6-12' },
  { year: '2026', event: 'Introduced Group Study, Mock Exams, and Textbook OCR' },
];

export default function AboutUsPage({ onBack }) {
  return (
    <div className="min-h-screen bg-[#0f172a] pb-16">
      {/* Header */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button 
          data-testid="back-from-about" 
          onClick={onBack} 
          className="text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-white font-['Outfit']">About StudyClaw</h1>
      </div>

      <div className="px-4 max-w-2xl mx-auto space-y-8">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-8"
        >
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-600 to-violet-700 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-violet-500/30">
            <Zap className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-3">
            Empowering Students to
            <span className="block bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
              Learn Smarter
            </span>
          </h2>
          <p className="text-slate-400 max-w-md mx-auto">
            StudyClaw is India's first AI-powered exam preparation platform designed specifically for CBSE students in Classes 6-12.
          </p>
        </motion.div>

        {/* Our Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6"
        >
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-violet-400" /> Our Story
          </h3>
          <div className="space-y-4 text-sm text-slate-400">
            <p>
              We started StudyClaw after witnessing a common problem: students struggling to revise effectively, 
              parents feeling disconnected from their child's education, and teachers unable to track who actually 
              understood the lesson.
            </p>
            <p>
              Our solution? An AI-powered platform that bridges the gap between school and home. When teachers 
              update chapter progress, students automatically receive smart revision nudges. Parents can see 
              exactly where their child stands. And students? They get instant access to AI-generated summaries, 
              quizzes, and even video explainers.
            </p>
            <p>
              Today, StudyClaw covers 351+ NCERT chapters across 6 subjects, serving thousands of students 
              preparing for their exams.
            </p>
          </div>
        </motion.div>

        {/* Our Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-400" /> What We Stand For
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {teamValues.map((value, i) => (
              <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center mb-3">
                  <value.icon className="w-5 h-5 text-violet-400" />
                </div>
                <h4 className="text-sm font-bold text-white mb-1">{value.title}</h4>
                <p className="text-xs text-slate-500">{value.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6"
        >
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Rocket className="w-5 h-5 text-cyan-400" /> Our Journey
          </h3>
          <div className="space-y-4">
            {milestones.map((m, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-16 shrink-0">
                  <span className="text-sm font-bold text-violet-400">{m.year}</span>
                </div>
                <div className="flex-1 pb-4 border-l-2 border-slate-800 pl-4 relative">
                  <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-violet-500" />
                  <p className="text-sm text-slate-300">{m.event}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/30 rounded-2xl p-6 text-center"
        >
          <h3 className="text-lg font-bold text-white mb-2">Get in Touch</h3>
          <p className="text-sm text-slate-400 mb-4">
            Have questions, feedback, or partnership inquiries?
          </p>
          <a 
            href="mailto:hello@studyclaw.io" 
            className="text-violet-400 hover:text-violet-300 text-sm font-medium"
          >
            hello@studyclaw.io
          </a>
        </motion.div>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-600">
          © 2026 StudyClaw. Made with ❤️ in India.
        </p>
      </div>
    </div>
  );
}
